import { Pipe, PipeTransform, Optional, Inject } from '@angular/core';
import { format, lightFormat } from 'date-fns';
import { enAU } from 'date-fns/locale';
import parseISO from 'date-fns/parseISO';

type Type = 'dob' | 'event' | 'timestamp' | 'custom';

@Pipe({
  name: 'caDate',
  pure: true,
})
export class DatePipe implements PipeTransform {
  constructor(
    @Optional()
    @Inject('window')
    public win?
  ) {
    this.win = win || window;
  }

  public transform(value: Date | string, type: Type, customFormat?: string) {
    if (!value) {
      return undefined;
    }
    // :Note: (Trent Bowden), if we would like any other locale than 'en-US',
    // we must use format instead of lightFormat, and pass in a localize object
    // with functions to localize (numbers, eras, quartes, months, days, periods)..
    // const locale: string =
    // this.win.navigator.language || (<any>this.win.navigator).userLanguage || 'en-au';

    // If a string has been passed in, convert it to a date before continuing.
    if (typeof value === 'string') {
      //CA-1377 underlying cause of parse failure is we have some dates in the database
      // formatted yyyy/mm/dd, which is not valid ISO date format.
      // So we are going to replace / with - to bring it back into line
      value = value.replace(/\//g, '-');

      let parsedDate;
      try {
        parsedDate = parseISO(value);
      } catch {
        // CA-1377 if we can't parse the date then we can't format the date
        return undefined;
      }

      // CA-1377 parseISO returns the string "Invalid Date" if the parsing fails
      // If we can't parse the date we can't format the date, and so we will
      // just return the toString of whatever was passed in. This is most likely
      // something that a human can interpret as a date
      if (!!parsedDate && parsedDate !== NaN && parsedDate.toString() !== 'Invalid Date') {
        value = parsedDate;
      } else {
        return value?.toString();
      }
    }

    value = new Date(value);

    switch (type) {
      case 'dob':
        // :Note: (Trent Bowden), Be wary of Date-fns using different tokens, see:
        // https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
        return lightFormat(value, 'd/M/yyyy');
      case 'event': {
        return format(value, 'd MMM');
      }
      case 'timestamp': {
        return lightFormat(value, 'd/M/yyyy h:mm a');
      }
      case 'custom': {
        if (!!customFormat) {
          // :Note: (Trent Bowden), we use format instead of lightFormat because
          // a custom format passed in can contain month names, etc.
          return format(value, customFormat, { locale: enAU });
        }
      }
      default: {
        return undefined;
      }
    }
  }
}
